<template>
  <div>
    <v-sheet class="transparent-background-color">
      <v-card class="page-card">
        <v-card-title class="mx-3">
          {{ $t("CRUD.editInfo", [$t("Dish.ObjectName")]) }}
          <v-spacer></v-spacer>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                @click="
                  $emit('mode', null);
                  $emit('input', null);
                "
                >fa-times
              </v-icon>
            </template>
            <span>
              {{ $t("CRUD.backToList", [$t("Dish.ObjectName")]) }}
            </span>
          </v-tooltip>
        </v-card-title>
        <v-progress-linear :indeterminate="loading"></v-progress-linear>
        <v-card-text>
          <ValidationObserver ref="observer">
            <v-row align="center">
              <v-col cols="3" class="font-weight-bold text-right">
                {{ $t("Dish.Fields.dish_code") + $t("Global.colon") }}
                <Required :show="savable" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <ValidationProvider
                  v-slot="{ errors }"
                  :name="$t('Dish.Fields.dish_code')"
                  rules="required"
                >
                  <v-text-field
                    single-line
                    v-model="dish.food_code"
                    :hint="isNew ? '' : $t('Dish.codeHint')"
                    :error-messages="
                      $resparse
                        .getErrorMsgs(serverErrors, 'food_code')
                        .concat(errors)
                    "
                  >
                    <template v-slot:message="{ key, message }">
                      <div :class="key == 0 ? 'danger--text' : ''">
                        {{ message }}
                      </div>
                    </template>
                  </v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="9" md="7" v-else>
                {{ dish.food_code }}
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="3" class="font-weight-bold text-right">
                {{ $t("Dish.Fields.dish_name") + $t("Global.colon") }}
                <Required :show="savable" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <ValidationProvider
                  v-slot="{ errors }"
                  :name="$t('Dish.Fields.dish_name')"
                  rules="required"
                >
                  <v-text-field
                    single-line
                    v-model="dish.food_name"
                    :hint="isNew ? '' : $t('Dish.nameHint')"
                    :error-messages="
                      $resparse
                        .getErrorMsgs(serverErrors, 'food_name')
                        .concat(errors)
                    "
                  >
                    <template v-slot:message="{ key, message }">
                      <div :class="key == 0 ? 'danger--text' : ''">
                        {{ message }}
                      </div>
                    </template>
                  </v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="9" md="7" v-else>
                {{ dish.food_name }}
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3" class="font-weight-bold text-right">
                {{ $t("Dish.Fields.dish_img") + $t("Global.colon") }}
                <Required :show="false" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7">
                <v-card v-if="dish_img" max-width="240">
                  <v-img
                    :src="dish_img"
                    @click.stop="$showFullscreenImages([dish_img])"
                  >
                    <v-tooltip right v-if="savable && dish_img">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          v-on="on"
                          color="secondary"
                          class="pa-2 float-right"
                          @click.stop="
                            dish.food_img = dish_img = dish.food_img_url = null
                          "
                        >
                          fa-times
                        </v-icon>
                      </template>
                      <span> {{ $t("Dish.delImg") }}</span>
                    </v-tooltip>
                  </v-img>
                </v-card>
                <v-file-input
                  ref="dish_img_file"
                  v-if="savable"
                  show-size
                  prepend-icon="fa-camera"
                  accept="image/png, image/jpeg"
                  persistent-hint
                  :placeholder="$t('Dish.imgPlaceholder')"
                  :hint="$t('Dish.imgHint')"
                  :rules="imgRules"
                  v-model="dish_img_file"
                >
                </v-file-input>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3" class="font-weight-bold text-right">
                {{ $t("Dish.Fields.dish_album") + $t("Global.colon") }}
                <Required :show="false" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7">
                <v-card height="320" v-if="dish_album && dish_album.length > 0">
                  <v-carousel hide-delimiters cycle height="320">
                    <v-carousel-item
                      v-for="(item, i) in dish_album"
                      :key="i"
                      :src="item"
                      @click.stop="$showFullscreenImages(dish_album, i)"
                    >
                      <v-tooltip right v-if="savable && item">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="secondary"
                            class="pa-2 float-right"
                            @click.stop="removeFromImages(i)"
                          >
                            fa-times
                          </v-icon>
                        </template>
                        <span> {{ $t("Dish.delImg") }}</span>
                      </v-tooltip>
                    </v-carousel-item>
                  </v-carousel>
                </v-card>
                <v-file-input
                  ref="dish_imgs_file"
                  v-if="savable"
                  show-size
                  counter
                  small-chips
                  multiple
                  suffix
                  prepend-icon="fa-camera"
                  accept="image/png, image/jpeg"
                  persistent-hint
                  :placeholder="$t('Dish.imgsPlaceholder')"
                  :hint="$t('Dish.imgsHint')"
                  :rules="imgsRules"
                  v-model="dish_imgs_file"
                >
                </v-file-input>
              </v-col>
              <v-col
                class="float-right"
                v-if="savable && dish_album && dish_album.length > 0"
              >
                <v-btn
                  color="primary"
                  x-small
                  @click.stop="
                    dish_album = [];
                    dish.food_album = [];
                  "
                >
                  {{ $t("Material.Fields.clear_all_imgs") }}
                </v-btn>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="3" class="font-weight-bold text-right">
                {{ $t("Dish.Fields.dish_category") + $t("Global.colon") }}
                <Required :show="savable" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <ValidationProvider
                  v-slot="{ errors }"
                  :name="$t('Dish.Fields.dish_category')"
                  rules="required"
                >
                  <SearcherDishCategory
                    v-model="foodCategorySelected"
                    no-label
                    unclearable
                    :error-messages="
                      $resparse
                        .getErrorMsgs(serverErrors, 'category_code')
                        .concat(errors)
                    "
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="9" md="7" v-else>
                {{ dish && dish.food_category ? dish.food_category.name : "" }}
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="3" class="font-weight-bold text-right">
                {{ $t("Dish.Fields.dish_unit") + $t("Global.colon") }}
                <Required :show="isNew" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="isNew">
                <ValidationProvider
                  v-slot="{ errors }"
                  :name="$t('Dish.Fields.dish_unit')"
                  rules="required"
                >
                  <v-text-field
                    single-line
                    v-model="dish.unit"
                    :error-messages="
                      $resparse
                        .getErrorMsgs(serverErrors, 'dish_unit')
                        .concat(errors)
                    "
                  >
                    <template v-slot:message="{ key, message }">
                      <div :class="key == 0 ? 'danger--text' : ''">
                        {{ message }}
                      </div>
                    </template>
                  </v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="9" md="7" v-else>
                {{ dish.unit }}
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="3" class="font-weight-bold text-right">
                {{ $t("Dish.Fields.sell_price") + $t("Global.colon") }}
                <Required :show="false" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <v-text-field
                  single-line
                  v-model="dish.price"
                  type="number"
                  min="0"
                  @blur="dish.price = $numberFormatter.formatMoney(dish.price)"
                  :prefix="$t('Global.money')"
                  @change="
                    dish.price > 0
                      ? (dish.cost_rate = $numberFormatter.formatTwoDot(
                          ((dish.price - dish.cost) / dish.price) * 100,
                          true
                        ))
                      : (dish.cost_rate = 0)
                  "
                />
              </v-col>
              <v-col cols="9" md="7" v-else>
                {{ $t("Global.money") }} {{ dish.price }}
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="3" class="font-weight-bold text-right">
                {{ $t("Dish.Fields.cost_price") + $t("Global.colon") }}
                <Required :show="false" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <v-text-field
                  single-line
                  v-model="dish.cost"
                  type="number"
                  min="0"
                  @blur="dish.cost = $numberFormatter.formatMoney(dish.cost)"
                  :prefix="$t('Global.money')"
                  @change="
                    dish.price > 0
                      ? (dish.cost_rate = $numberFormatter.formatTwoDot(
                          ((dish.price - dish.cost) / dish.price) * 100,
                          true
                        ))
                      : (dish.cost_rate = 0)
                  "
                />
              </v-col>
              <v-col cols="9" md="7" v-else>
                {{ $t("Global.money") }} {{ dish.cost }}
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="3" class="font-weight-bold text-right">
                {{ $t("Dish.Fields.gross_profit_rate") + $t("Global.colon") }}
                <Required :show="false" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <v-text-field
                  single-line
                  v-model="dish.cost_rate"
                  type="number"
                  min="0"
                  @blur="
                    dish.cost_rate = $numberFormatter.formatTwoDot(
                      dish.cost_rate,
                      true
                    )
                  "
                  :suffix="$t('Global.rate')"
                  disabled
                />
              </v-col>
              <v-col cols="9" md="7" v-else>
                {{ dish.cost_rate }} {{ $t("Global.rate") }}
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="3" class="font-weight-bold text-right">
                {{ $t("Dish.Fields.eat_in_cost") + $t("Global.colon") }}
                <Required :show="false" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <v-text-field
                  single-line
                  v-model="dish.room_cost"
                  type="number"
                  min="0"
                  @blur="
                    dish.room_cost = $numberFormatter.formatMoney(
                      dish.room_cost
                    )
                  "
                  :prefix="$t('Global.money')"
                />
              </v-col>
              <v-col cols="9" md="7" v-else>
                {{ $t("Global.money") }} {{ dish.room_cost }}
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="3" class="font-weight-bold text-right">
                {{ $t("Dish.Fields.takeout_cost") + $t("Global.colon") }}
                <Required :show="false" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <v-text-field
                  single-line
                  v-model="dish.takeout_cost"
                  type="number"
                  min="0"
                  @blur="
                    dish.takeout_cost = $numberFormatter.formatMoney(
                      dish.takeout_cost
                    )
                  "
                  :prefix="$t('Global.money')"
                />
              </v-col>
              <v-col cols="9" md="7" v-else>
                {{ $t("Global.money") }} {{ dish.takeout_cost }}
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="3" class="font-weight-bold text-right">
                {{ $t("Dish.Fields.sale_enable") + $t("Global.colon") }}
                <Required :show="false" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <v-radio-group row v-model="dish.sale_enable">
                  <v-radio
                    :label="$t('Dish.sale_enable.on')"
                    :key="1"
                    :value="1"
                  />
                  <v-radio
                    :label="$t('Dish.sale_enable.off')"
                    :key="0"
                    :value="0"
                  />
                </v-radio-group>
              </v-col>
              <v-col cols="9" md="7" v-else>
                <v-chip
                  small
                  label
                  dark
                  :color="dish.sale_enable ? 'primary' : 'grey'"
                >
                  {{
                    dish.sale_enable
                      ? $t("Dish.sale_enable.on")
                      : $t("Dish.sale_enable.off")
                  }}
                </v-chip>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="3" class="font-weight-bold text-right">
                {{
                  $t("Dish.Fields.dish_practice") +
                  " / " +
                  $t("Dish.Fields.cost_card") +
                  $t("Global.colon")
                }}
                <Required :show="false" :mode="mode" />
              </v-col>
              <v-col cols="9" md="7" v-if="savable">
                <v-row dense>
                  <v-col
                    v-for="(item, index) in dish.food_relations"
                    :key="item.id"
                    cols="12"
                    sm="6"
                  >
                    <FoodRelations
                      v-model="dish.food_relations[index]"
                      @delete="
                        dish.food_relations.splice(
                          dish.food_relations.findIndex((detail) =>
                            $event.id
                              ? $event.id == detail.id
                              : $event.new == detail.new
                          ),
                          1
                        )
                      "
                    />
                  </v-col>
                  <v-col cols="12" sm="6" v-if="savable">
                    <EditRelations
                      :relations="dish.food_relations"
                      @updateRelations="dish.relations = $event"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="9" md="7" v-else>
                <v-row v-for="item in dish.food_relations" :key="item.id">
                  <v-col>
                    <a
                      class="text-wrap text-caption"
                      text-color="primary"
                      style="text-decoration: none"
                      @click="
                        CostCardInfoDialog = true;
                        currentFormulaId = item.food_formula_id;
                      "
                    >
                      {{
                        item.food_behavior.behavior_name +
                        " / " +
                        item.food_formula.formula_name
                      }}
                    </a>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </ValidationObserver>
        </v-card-text>
      </v-card>

      <v-card class="page-opts">
        <v-card-title>
          <v-row dense>
            <v-col cols="12">
              <v-btn
                outlined
                color="secondary"
                class="elevation-1"
                block
                @click="
                  $emit('input', null);
                  $emit('mode', null);
                  $emit('save');
                "
              >
                <v-icon small class="pe-1">fa-times</v-icon>
                {{ $t("CRUD.backToList", [$t("Dish.ObjectName")]) }}
              </v-btn>
            </v-col>
            <v-col
              cols="12"
              v-if="isView && $permission.permissionAllow('Foods', 'edit')"
            >
              <v-btn
                color="accent"
                block
                :disabled="loading && !dish.id"
                @click="$emit('mode', 'edit')"
              >
                <v-icon small class="pe-1">fa-pencil-alt</v-icon>
                {{ $t("CRUD.editInfo", [$t("Dish.ObjectName")]) }}
              </v-btn>
            </v-col>
            <v-col cols="12" v-if="savable">
              <v-btn
                color="success"
                block
                :disabled="(loading && !dish.id) || isUploading"
                @click="submit()"
              >
                <v-icon small class="pe-1">fa-save</v-icon>
                {{ $t("CRUD.saveInfo", [$t("Dish.ObjectName")]) }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>
      </v-card>
    </v-sheet>

    <CostCardInfoDialog
      v-model="CostCardInfoDialog"
      :formulaId="currentFormulaId"
    />
  </div>
</template>
<script>
import i18n from "@/i18n";
import { mapState } from "vuex";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import Required from "@/components/plugins/Required";
import SearcherDishCategory from "@/components/plugins/SearcherDishCategory";
// import SearcherCookingMethod from "@/components/plugins/SearcherCookingMethod";
// import SearcherRecipe from "@/components/plugins/SearcherRecipe";
import FoodRelations from "./FoodRelations";
import EditRelations from "./EditRelations";
import CostCardInfoDialog from "./CostCardInfoDialog";
import Dishes from "@/api/dishes";
import Files from "@/api/files";

export default {
  name: "RecipeDetail",

  props: ["value", "mode"],

  components: {
    ValidationObserver,
    ValidationProvider,
    Required,
    SearcherDishCategory,
    // SearcherCookingMethod,
    // SearcherRecipe,
    FoodRelations,
    EditRelations,
    CostCardInfoDialog,
  },

  computed: {
    isView() {
      return !this.mode || this.mode == "view";
    },
    savable() {
      return this.mode == "edit" || this.mode == "new" || this.mode == "clone";
    },
    isNew() {
      return this.mode == "new" || this.mode == "clone";
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "stickTableCols"]),
  },

  data: () => ({
    dish: {},
    dishes: [],
    foodCategorySelected: {},
    serverErrors: [],
    costCardInfo: [],
    editCostCardBtn: false,
    CostCardInfoDialog: false,
    currentFormulaId: "",
    currentItem: null,
    imgRules: [
      (value) => !value || value.size < 5000000 || i18n.t("Material.imgHint"),
    ],
    dish_img: "",
    dish_img_file: null,
    imgsRules: [
      (value) =>
        !value ||
        value.filter((v) => v.size >= 10000000).length == 0 ||
        i18n.t("Material.imgsHint"),
    ],
    dish_album: [],
    dish_imgs_file: [],
    isUploading: false,
  }),

  methods: {
    submit() {
      this.$store.dispatch("fullscreenLoading");
      this.serverErrors = []; //重置远端错误提示
      this.$refs.observer.validate().then(
        async function (result) {
          if (result) {
            //最后提交商品信息
            Dishes.save(this.dish.id, this.dish)
              .then(
                function (response) {
                  if (response.data.status.isOK()) {
                    this.$emit("save", this.material);
                    this.$emit("mode", null);
                    this.$emit("input", null);
                  } else if (
                    response.data.error &&
                    response.data.error.data &&
                    "ERR333" == response.data.error.code
                  ) {
                    this.serverErrors = response.data.error.data;
                  }
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              )
              .catch(
                function () {
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              );
          } else {
            this.$store.dispatch("fullscreenLoadingStop");
          }
        }.bind(this)
      );
    },
    removeFromImages(i) {
      this.dish_album.splice(i, 1);
      if (this.dish.food_album && i < this.dish.food_album.length) {
        this.dish.food_album.splice(i, 1);
        this.dish.food_album_url.splice(i, 1);
      }
    },
  },

  watch: {
    value: {
      immediate: true, //初始赋值也调用
      handler() {
        this.dish_img = "";
        this.dish_img_file = null;
        this.dish_album = [];
        this.dish_imgs_file = [];
        if (this.value && this.value.id) {
          Dishes.fetchOne(this.value.id).then(
            function (response) {
              if (response.data.status.isOK()) {
                this.dish = response.data.food;
                this.dish.relations = this.dish.food_relations; //保存的时候，传的是relations
                this.foodCategorySelected = this.dish.food_category;
                this.dish_img = this.dish.food_img_url;
                this.dish_album = this.dish.food_album_url
                  ? JSON.parse(JSON.stringify(this.dish.food_album_url))
                  : [];
              }
            }.bind(this)
          );
        } else {
          // 新建菜品，初始化默认值
          this.dish = {
            food_relations: [],
            sale_enable: 1,
          };
          this.foodCategorySelected = null;
        }
        this.$scrollTop(); //返回顶部
      },
    },
    foodCategorySelected: {
      handler() {
        this.dish.food_category_id = this.foodCategorySelected
          ? this.foodCategorySelected.id
          : "";
      },
    },
    dish_img: {
      handler() {
        if (!this.dish_img) {
          this.dish_img_file = null;
        }
      },
    },
    dish_img_file: {
      handler() {
        if (this.dish_img_file) {
          this.isUploading = true;
          // 检查一下图片上传的error，拒绝上传
          if (this.$refs.dish_img_file.hasError) {
            this.isUploading = false;
            return;
          }
          Files.imageUpload(this.dish_img_file, false, "foods")
            .then(
              function (response) {
                if (response.data.status.isOK()) {
                  this.dish.food_img = response.data.data.img_name;
                  this.dish_img = response.data.data.img_url;
                  this.isUploading = false;
                  this.dish_img_file = null;
                }
              }.bind(this)
            )
            .catch(
              function () {
                this.isUploading = false;
              }.bind(this)
            );
        }
      },
    },
    dish_imgs_file: {
      handler() {
        if (this.dish_imgs_file && this.dish_imgs_file.length > 0) {
          this.isUploading = true;
          // 检查一下图片上传的error，拒绝上传
          if (this.$refs.dish_imgs_file.hasError) {
            this.isUploading = false;
            return;
          }
          //再尝试提交图片集
          Files.imagesUpload(this.dish_imgs_file, false, "foods")
            .then(
              function (response) {
                if (response.data.status.isOK()) {
                  if (
                    !this.dish.food_album_url ||
                    !this.dish.food_album ||
                    !this.dish_album
                  ) {
                    this.dish.food_album_url = [];
                    this.dish.food_album = [];
                    this.dish_album = [];
                  }
                  response.data.data.forEach((img, i) => {
                    this.dish.food_album.push(img.img_name);
                    this.dish_album.splice(
                      (this.dish.food_album_url
                        ? this.dish.food_album_url.length
                        : 0) + i,
                      0,
                      img.img_url
                    );
                  });
                }
                this.isUploading = false;
                this.dish_imgs_file = [];
              }.bind(this)
            )
            .catch(
              function () {
                this.isUploading = false;
              }.bind(this)
            );
        }
      },
    },
  },
};
</script>